import React, { useState, useEffect } from 'react';
import { ShieldAlert, X, MessageCircle, Search, ThumbsUp, AlertTriangle, Zap, CheckCircle2, Crosshair, Flame, TrendingUp, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../../config';
const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

const XHSRadar = ({ brandProjectId, dateRange, startDate, endDate }) => {
  const [data, setData] = useState({ serpData: [], radarList: [], metrics: {} });
  const [loading, setLoading] = useState(true);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState('');
  const [drawerFilter, setDrawerFilter] = useState('all');

  const openDrawer = (title, filterType) => {
    setDrawerTitle(title);
    setDrawerFilter(filterType);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };


  useEffect(() => {
    // If not passed via props, try fallback locations
    const query = new URLSearchParams(window.location.search);
    const effectiveBrandId = brandProjectId || query.get('brandProjectId') || localStorage.getItem('globalBrand');
    
    if (effectiveBrandId && effectiveBrandId !== 'all') {
      setLoading(true);
      fetchData(effectiveBrandId);
    } else {
      setLoading(false);
    }
  }, [brandProjectId, dateRange, startDate, endDate]);

  const fetchData = async (id) => {
    try {
      const token = localStorage.getItem('token');
      
      let url = `${API_URL}/seo-ecology/xhs-radar?brandProjectId=${id}`;
      if (dateRange === 'custom') {
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;
        url += `&dateRange=custom`;
      } else {
        url += `&dateRange=${dateRange || 30}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Failed to fetch XHS Radar:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    switch(level) {
      case 'critical': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
      case 'hijack': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'warning': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'safe': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'danger': return <Crosshair className="w-4 h-4 text-orange-500" />;
      case 'critical': return <ShieldAlert className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const formatDate = (isoString) => {
    if(!isoString) return '刚刚';
    const date = new Date(isoString);
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-4" />
        <p className="text-slate-500 dark:text-slate-400">正在接入小红书真实舆情数据库...</p>
      </div>
    );
  }

  const { metrics, radarList, serpData } = data;
  const isDataEmpty = !serpData || serpData.length === 0;

  const displayMetrics = [
    { label: '监控主笔记样本', value: `${metrics.totalNotes || 0}篇`, icon: <Search className="w-5 h-5 text-blue-500" />, desc: '品牌大盘辐射广度' },
    { label: '深巡评论总数', value: `${metrics.totalComments || 0}条`, icon: <MessageCircle className="w-5 h-5 text-indigo-500" />, desc: '网民真实口碑穿透' },
    { label: '舆情风险总数', value: `${metrics.radarCount || 0}条`, icon: <Flame className="w-5 h-5 text-orange-500" />, desc: '公关待处理池', filterType: 'all', title: '小红书 - 舆情风险总盘' },
    { label: '极危负面/侵权', value: `${metrics.criticalCount || 0}条`, icon: <AlertTriangle className="w-5 h-5 text-red-500" />, desc: '高危痛点需立即压制', filterType: 'critical', title: '小红书 - 极危负面明细' },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {displayMetrics.map((m, i) => (
          
          <div 
            key={i} 
            className={`bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-start gap-4 ${m.filterType ? 'cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all' : ''}`}
            onClick={() => { if (m.filterType) openDrawer(m.title, m.filterType); }}
          >
            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg shrink-0">
              {m.icon}
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{m.value}</div>
              <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">{m.label}</div>
              <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">{m.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {isDataEmpty ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-16 text-center shadow-sm">
          <ShieldAlert className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-700 dark:text-slate-200 mb-2">当前品牌暂无小红书生态数据</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">需在「抓取监控中心」配置包含“小红书”引擎的监控任务，数据清洗后将自动同步至此面板。</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Comment Radar */}
          <div className="lg:col-span-7 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col h-[700px]">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
              <div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-red-500" />
                  高危笔记与内容情报流
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">系统已将真实的负面及竞品截流内容，按点赞量降序拦截</p>
              </div>
            </div>
            <div className="p-5 space-y-4 flex-1 overflow-y-auto custom-scrollbar">
              {radarList && radarList.length > 0 ? radarList.map((c, idx) => (
                <div key={idx} className="border border-slate-100 dark:border-slate-700 rounded-xl p-4 bg-slate-50/30 dark:bg-slate-900/30 hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-md border font-bold ${getLevelColor(c.level)}`}>
                        {c.levelText}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> 检索词: <span className="font-semibold text-slate-700 dark:text-slate-300">{c.keyword}</span> ({c.rank})
                      </span>
                    </div>
                    <span className="text-xs text-slate-400">{formatDate(c.time)}</span>
                  </div>
                  
                  <div className="mb-2 text-sm text-slate-600 dark:text-slate-400 font-semibold flex items-start gap-2">
                    <span className="shrink-0 mt-0.5">源笔记：</span>
                    <a href={c.sourceLink || '#'} target="_blank" rel="noreferrer" className="text-slate-800 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 underline decoration-slate-300 dark:decoration-slate-600 underline-offset-4 line-clamp-2">
                      {c.noteTitle}
                    </a>
                  </div>
                  
                  <div className={`bg-white dark:bg-slate-950 p-4 rounded-lg border ${c.levelText?.includes('评论') ? 'border-rose-300 dark:border-rose-800 shadow-sm shadow-rose-500/10' : 'border-slate-200 dark:border-slate-800'} mb-3 relative`}>
                    {c.levelText?.includes('评论') && (
                        <div className="absolute -top-3 left-4 bg-rose-500 text-white text-[11px] font-black px-3 py-1 rounded-full shadow-sm flex items-center gap-1 border-2 border-white dark:border-slate-800">
                          <MessageCircle className="w-3 h-3" /> 评论区踩雷
                        </div>
                    )}
                    <div className="absolute top-3 right-3 flex items-center gap-1 text-red-500 bg-red-50 dark:bg-red-500/10 px-2 py-0.5 rounded text-xs font-bold">
                      <ThumbsUp className="w-3 h-3" /> {c.likes} 赞
                    </div>
                    <div className={`text-sm leading-relaxed pr-16 ${c.levelText?.includes('评论') ? 'text-rose-900 dark:text-rose-200 font-medium mt-2' : 'text-slate-700 dark:text-slate-300'}`}>
                      {c.levelText?.includes('评论') ? (
                        <div className="flex gap-2 items-start">
                           <span className="text-rose-400 text-lg leading-none shrink-0">"</span>
                           <span>{c.content}</span>
                           <span className="text-rose-400 text-lg leading-none shrink-0">"</span>
                        </div>
                      ) : (
                        c.content
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 text-sm bg-indigo-50/50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800/30">
                    <EyeOff className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-bold text-indigo-700 dark:text-indigo-400 block mb-0.5">思序AI应对建议：</span>
                      <span className="text-indigo-600 dark:text-indigo-300 leading-relaxed">{c.aiSuggestion}</span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-10 text-slate-500">当前没有高危负面预警。</div>
              )}
            </div>
          </div>

          {/* Right Column: SERP Defense */}
          <div className="lg:col-span-5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col h-[700px]">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Crosshair className="w-5 h-5 text-indigo-500" />
                搜索占位与竞品入侵榜
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">基于库内抓取的真实小红书搜索排位数据映射</p>
            </div>
            
            <div className="p-5 space-y-6 overflow-y-auto custom-scrollbar">
              {serpData.map((s, idx) => (
                <div key={idx} className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
                    <Search className="w-4 h-4 text-slate-400" />
                    <h4 className="font-bold text-slate-800 dark:text-slate-200">{s.keyword}</h4>
                  </div>
                  
                  <div className="space-y-2 pl-2 border-l-2 border-slate-100 dark:border-slate-700">
                    {s.results.map((r, i) => (
                      <div key={i} className="pl-3 py-2 relative group hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors">
                        <div className="absolute -left-[9px] top-4 w-4 h-4 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center">
                          <div className={`w-2 h-2 rounded-full ${r.status === 'safe' ? 'bg-emerald-500' : r.status === 'danger' ? 'bg-orange-500' : r.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                        </div>
                        
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-400 dark:text-slate-500">#{r.rank}</span>
                            <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded">{r.type}</span>
                          </div>
                          {getStatusIcon(r.status)}
                        </div>
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 line-clamp-2 mb-1 leading-snug" title={r.title}>{r.title}</div>
                        <div className={`text-xs ${r.status === 'safe' ? 'text-emerald-600 dark:text-emerald-400' : r.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : r.status === 'danger' ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400'}`}>
                          {r.desc}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* Evidence Drawer Overlay & Content */}
      <div className={`fixed inset-0 z-[60] transition-opacity duration-300 ${isDrawerOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeDrawer}></div>
        
        {/* Drawer Panel */}
        <div className={`absolute top-0 right-0 h-full w-full sm:w-[500px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl transition-transform duration-300 transform flex flex-col ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
              {drawerFilter === 'critical' ? <AlertTriangle className="w-5 h-5 text-rose-500" /> : <MessageCircle className="w-5 h-5 text-emerald-500" />}
              {drawerTitle}
            </h3>
            <button onClick={closeDrawer} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* List Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-slate-50/30 dark:bg-slate-950/30">
            {(() => {
              // Filter logic
              let items = radarList || [];
              if (drawerFilter === 'critical') {
                items = items.filter(c => c.level === 'critical');
              } else {
                // all high-risk items are already in radarList, we can just show them
              }
              // Sort by likes descending
              const levelScore = { 'critical': 3, 'warning': 2, 'hijack': 1, 'info': 0 };
              items = [...items].sort((a, b) => {
                  const scoreA = levelScore[a.level] || 0;
                  const scoreB = levelScore[b.level] || 0;
                  if (scoreA !== scoreB) return scoreB - scoreA;
                  return (b.likes || 0) - (a.likes || 0);
              });

              if (items.length === 0) {
                return <div className="text-center py-20 text-slate-500 dark:text-slate-400">暂无符合条件的预警数据</div>;
              }

              return items.map((c, idx) => {
                const isCritical = c.level === 'critical';
                return (
                  <div key={idx} className={`bg-white dark:bg-slate-800 rounded-xl p-5 border shadow-sm transition-shadow ${isCritical ? 'border-rose-200 dark:border-rose-900/50' : 'border-slate-200 dark:border-slate-700'}`}>
                    
                    {/* Top Stats */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-md font-bold ${getLevelColor(c.level)}`}>
                          {c.levelText}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {formatDate(c.time)}
                        </span>
                      </div>
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold ${isCritical ? 'bg-rose-50 text-rose-600 dark:bg-rose-500/10' : 'bg-slate-100 text-slate-600 dark:bg-slate-800'}`}>
                        <ThumbsUp className="w-3 h-3" /> {c.likes || 0} 赞
                      </div>
                    </div>

                    {/* Author / Source */}
                    <div className="mb-2 text-sm text-slate-600 dark:text-slate-400 truncate font-medium">
                      源笔记：<span className="text-slate-800 dark:text-slate-200">{c.noteTitle}</span>
                    </div>


                    {/* Content Snippet */}
                    <div className="bg-slate-50 dark:bg-slate-900 p-3.5 rounded-lg border border-slate-100 dark:border-slate-800 mb-4 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                      {c.content}
                    </div>

                    {/* AI Suggestion */}
                    <div className="flex items-start gap-2 text-sm bg-indigo-50/50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800/30 mb-4">
                      <EyeOff className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                      <div>
                        <span className="font-bold text-indigo-700 dark:text-indigo-400 block mb-0.5">思序AI判定与建议：</span>
                        <span className="text-indigo-600 dark:text-indigo-300 leading-relaxed">{c.aiSuggestion}</span>
                      </div>
                    </div>


                    {/* Actions */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700/50">
                      {c.sourceLink ? (
                        <a href={c.sourceLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 font-semibold flex items-center gap-1 transition-colors">
                          <Zap className="w-4 h-4" /> 查看原贴
                        </a>
                      ) : (
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                           无原贴链接
                        </span>
                      )}
                      <button className="text-sm px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded transition-colors font-medium">
                        标记已阅
                      </button>
                    </div>

                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

    </div>
  );
};

export default XHSRadar;
